<div class="tile">
  <div class="body">
    <div class="column">
      <div class="header">
        <div class="title">
          <span class="label">{{ tile().title }}</span>
          @if (tile().subtitle) {
            <span class="subtitle">{{ tile().subtitle }}</span>
          }
        </div>

        @if (tile().primaryValue) {
          <div class="primary-value">
            <ng-container *ngTemplateOutlet="valueTemplate; context: {value: tile().primaryValue}"></ng-container>
          </div>
        }
      </div>

      <!--
        A single-segment bar gets no tooltip: the one row would only repeat the tile's own value.
        Hence the two wrappers — `[tippy]` needs a real template, a null one throws in the directive.
      -->
      @if (progressBar().length > 1) {
        <div class="progress-bar"
          [tippy]="progressBarTooltip"
          [tippyOptions]="tooltipOptions"
          [templateContext]="{progressBar: progressBar()}">
          <ng-container *ngTemplateOutlet="progressBarSegments"></ng-container>
        </div>
      } @else if (progressBar().length) {
        <div class="progress-bar">
          <ng-container *ngTemplateOutlet="progressBarSegments"></ng-container>
        </div>
      }

      @if (tile().secondaryValues?.length) {
        <div class="secondary-values">
          <ng-container *ngTemplateOutlet="valuesTemplate; context: {values: tile().secondaryValues}"></ng-container>
        </div>
      }
    </div>

    @if (tile().info) {
      <i class="material-icons-outlined info"
        [tippy]="infoTooltip"
        [tippyOptions]="tooltipOptions"
        [templateContext]="{title: tile().title, info: tile().info}">info</i>
    }

    @if (tile().nav?.routerLink && !clickableTile()) {
      <a class="action" [routerLink]="tile().nav?.routerLink" [queryParams]="tile().nav?.queryParams">
        <i class="material-icons-outlined" [style.transform]="'rotate(135deg)'">arrow_back_ios</i>
      </a>
    }
  </div>

  @if (tile().extension) {
    <div class="divider"></div>
    <div class="extension">
      @for (line of tile().extension; track $index) {
        <div class="extension-line">
          <ng-container *ngTemplateOutlet="valuesTemplate; context: {values: line}"></ng-container>
        </div>
      }
    </div>
  }

  @if (clickableTile() && tile().nav?.routerLink) {
    <a class="tile-link"
      [routerLink]="tile().nav?.routerLink"
      [queryParams]="tile().nav?.queryParams"
      [attr.aria-label]="tile().title"></a>
  }
</div>

<ng-template #progressBarSegments>
  @for (bar of progressBar(); track $index) {
    <div class="inner-progress-bar" [style.width.%]="bar.value" [style.background-color]="bar.color ?? ''"></div>
  }
</ng-template>

<ng-template #valuesTemplate let-values="values">
  @for (value of values; track $index) {
    <div class="value-wrapper">
      <ng-container *ngTemplateOutlet="valueTemplate; context: {value}"></ng-container>
    </div>
  }
</ng-template>

<!--
  One template per value shape, picked by which key the value carries (see `TileValue`). A shape's
  own field being `null` means "no data", which renders the shared N/A placeholder.
-->
<ng-template #valueTemplate let-value="value">
  @if (value.hasOwnProperty('label')) {
    <span class="label">{{ value.label }}</span>
  }

  @if (value.hasOwnProperty('text')) {
    @if (value.text === null) {
      <ng-container *ngTemplateOutlet="placeholderTemplate; context: {value}"></ng-container>
    } @else {
      <span class="value" [style.color]="value.color ?? ''" [style.font-size.px]="value.fontSize">
        @if (value.icon) {
          <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value.icon}"></ng-container>
        }
        <span class="text" [style.font-size]="'inherit'">{{ value.text }}</span>
        @if (value.subText) {
          <span class="sub-text" [style.font-size]="'inherit'">{{ value.subText }}</span>
        }
      </span>
    }
  } @else if (value.hasOwnProperty('number')) {
    @if (value.number === null) {
      <ng-container *ngTemplateOutlet="placeholderTemplate; context: {value}"></ng-container>
    } @else {
      <span class="value" [style.color]="value.color ?? ''" [style.font-size.px]="value.fontSize">
        @if (value.icon) {
          <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value.icon}"></ng-container>
        }
        {{ value.prefix ?? '' }}{{ value.number | formatNumber: 0 : 2 }}{{ value.unit ?? '' | formatUnit }}
      </span>
    }
  } @else if (value.hasOwnProperty('array')) {
    @if (value.array === null) {
      <ng-container *ngTemplateOutlet="placeholderTemplate; context: {value}"></ng-container>
    } @else {
      <span class="value" [style.color]="value.color ?? ''" [style.font-size.px]="value.fontSize">
        @if (value.icon) {
          <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value.icon}"></ng-container>
        }
        @for (entry of value.array; track $index) {
          {{ entry }}
          @if (!$last) {
            <span class="separator">{{ value.separator ?? '/' }}</span>
          }
        }
      </span>
    }
  } @else if (value.hasOwnProperty('date')) {
    @if (value.date === null) {
      <ng-container *ngTemplateOutlet="placeholderTemplate; context: {value}"></ng-container>
    } @else {
      <span class="value" [style.color]="value.color ?? ''" [style.font-size.px]="value.fontSize">
        @if (value.icon) {
          <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value.icon}"></ng-container>
        }
        {{ value.date | formatDate: value.dateFormat : true }}
      </span>
    }
  } @else if (value.hasOwnProperty('date1') && value.hasOwnProperty('date2')) {
    @if (value.date1 === null && value.date2 === null) {
      <ng-container *ngTemplateOutlet="placeholderTemplate; context: {value}"></ng-container>
    } @else {
      <span class="value" [style.color]="value.color ?? ''" [style.font-size.px]="value.fontSize">
        @if (value.icon) {
          <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value.icon}"></ng-container>
        }
        {{ value.date1 | formatDate: value.date1Format : true }}{{ value.separator ?? '-' }}{{ value.date2 | formatDate: value.date2Format : true }}
      </span>
    }
  } @else if (value.hasOwnProperty('symbol')) {
    <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value}"></ng-container>
  }

  @if (value.caption) {
    <span class="caption">{{ value.caption }}</span>
  }
</ng-template>

<!-- A value's leading symbol, or an extension row's: `TILE_DOT` is a circle, anything else a glyph. -->
<ng-template #symbolTemplate let-symbol="symbol">
  @if (symbol.symbol === dot) {
    <span class="symbol dot" [style.background-color]="symbol.color ?? ''"></span>
  } @else {
    <i class="material-symbols-outlined symbol"
      [class.filled]="symbol.filled"
      [style.color]="symbol.color ?? ''">{{ symbol.symbol }}</i>
  }
</ng-template>

<ng-template #placeholderTemplate let-value="value">
  <span class="value placeholder">
    @if (value.icon) {
      <ng-container *ngTemplateOutlet="symbolTemplate; context: {symbol: value.icon}"></ng-container>
    }
    <ng-container i18n>N/A</ng-container>
  </span>
</ng-template>

<ng-template #infoTooltip let-title="title" let-info="info">
  <div class="info-tooltip">
    @if (title) {
      <span class="info-title">{{ title }}</span>
    }
    <span>{{ info }}</span>
  </div>
</ng-template>

<ng-template #progressBarTooltip let-progressBar="progressBar">
  <div class="progress-bar-tooltip">
    @for (bar of progressBar; track $index) {
      <div class="tooltip-row">
        <span class="bar-swatch" [style.background-color]="bar.color"></span>
        <span><b>{{ bar.name }}:</b> {{ bar.value }}%</span>
      </div>
    }
  </div>
</ng-template>
